<template>
    <header>
        <nav class="navbar navbar-light">
            <div class="container">
                <a href="#" class="navbar-brand" @click.prevent="reload()">
                    <img :src="logo" v-if="logo" />
                </a>
                <div class="nav navbar-nav pull-xs-right header-tools">
                    <div class="clock">
                        <div v-if="showLanguagePicker" class="language-picker">
                            <div
                                class="language-picker-dropdown"
                                :class="{ 'language-picker-dropdown-open': isLanguagePickerOpen }"
                            >
                                <button
                                    :id="languagePickerId"
                                    type="button"
                                    class="language-picker-trigger"
                                    :aria-label="pickerAccessibilityLabel"
                                    :aria-expanded="isLanguagePickerOpen ? 'true' : 'false'"
                                    @click="toggleLanguagePicker"
                                >
                                    <span class="language-picker-trigger-content">
                                        <span
                                            v-if="currentLanguageOption && currentLanguageOption.flag"
                                            class="language-picker-flag"
                                        >
                                            <img
                                                :src="currentLanguageOption.flag"
                                                :alt="currentLanguageOption.label"
                                            />
                                        </span>
                                        <span
                                            v-else
                                            class="language-picker-flag language-picker-flag-fallback"
                                        >
                                            {{ currentLanguageOption ? currentLanguageOption.shortLabel : "?" }}
                                        </span>
                                        <span class="language-picker-trigger-label">
                                            {{ currentLanguageOption ? currentLanguageOption.label : pickerPlaceholder }}
                                        </span>
                                    </span>
                                    <i
                                        class="fa fa-fw fa-angle-down language-picker-chevron"
                                        :class="{ 'language-picker-chevron-open': isLanguagePickerOpen }"
                                    />
                                </button>
                                <div v-if="isLanguagePickerOpen" class="language-picker-menu">
                                    <button
                                        v-for="language in supportedLanguages"
                                        :key="language.code"
                                        type="button"
                                        class="language-picker-option"
                                        :class="{ 'language-picker-option-active': language.code === selectedLanguage }"
                                        @click="selectLanguage(language.code)"
                                    >
                                        <span
                                            v-if="language.flag"
                                            class="language-picker-flag"
                                        >
                                            <img :src="language.flag" :alt="language.label" />
                                        </span>
                                        <span
                                            v-else
                                            class="language-picker-flag language-picker-flag-fallback"
                                        >
                                            {{ language.shortLabel }}
                                        </span>
                                        <span class="language-picker-option-label">{{ language.label }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <span class="clock-time">
                            <i class="fa fa-fw fa-clock" /> {{ clock.time }}
                        </span>
                    </div>
                </div>
            </div>
        </nav>
    </header>
</template>

<style lang="scss" scoped>
header {

    position: relative;
    background: rgba(var(--color-header-background), 1);
    height: 100px;
    z-index: 1;

    .container {
        display: flex;
        align-items: center;
        height: 100%;
        position: relative;
    }

    .navbar {
        padding: 0;
        height: 100%;
    }

    .navbar-brand {

        margin: 0;
        padding: 0;
        position: relative;
        z-index: 2;

        img {
            height: 82px;
            margin: 0;

        }

    }

    .header-tools {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-left: auto;
        z-index: 1;
    }

    .clock {
        display: flex;
        align-items: center;
        gap: 24px;
        flex-wrap: nowrap;

        .language-picker {
            display: flex;
            align-items: center;
            position: relative;
        }

        .language-picker-dropdown {
            position: relative;
        }

        .language-picker-trigger {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            min-width: 160px;
            padding: 10px 14px;
            border-radius: 12px;
            border: 2px solid rgba(var(--color-language-picker-text), 0.85);
            background: rgba(var(--color-language-picker-background), 1);
            color: rgba(var(--color-language-picker-text), 1);
            font-size: 16px;
            font-weight: 600;
            outline: none;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }

        .language-picker-trigger-content {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
        }

        .language-picker-trigger-label {
            white-space: nowrap;
        }

        .language-picker-chevron {
            transition: transform 160ms ease;
        }

        .language-picker-chevron-open {
            transform: rotate(180deg);
        }

        .language-picker-menu {
            position: absolute;
            top: calc(100% + 10px);
            left: 0;
            min-width: 100%;
            padding: 10px 0;
            border-radius: 12px;
            background: rgba(var(--color-language-picker-background), 1);
            box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
        }

        .language-picker-option {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 14px;
            border: none;
            background: transparent;
            color: rgba(var(--color-language-picker-text), 1);
            font-size: 16px;
            font-weight: 500;
            text-align: left;
            outline: none;
        }

        .language-picker-option-active,
        .language-picker-option:hover {
            background: rgba(0, 0, 0, 0.06);
        }

        .language-picker-option-label {
            white-space: nowrap;
        }

        .language-picker-flag {
            width: 22px;
            height: 22px;
            flex: 0 0 22px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 999px;
            overflow: hidden;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
            background: rgba(255, 255, 255, 0.85);
        }

        .language-picker-flag img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .language-picker-flag-fallback {
            font-size: 10px;
            font-weight: 700;
        }

        .clock-time {
            font-size: 28px;
            color: rgba(var(--color-header-text), 1);

            i {
                opacity: 0.5;
            }
        }
    }

}
</style>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'Header',
    data() {
        return {
            logo: null,
            languagePickerId: 'kiosk-language-picker',
            isLanguagePickerOpen: false,
            selectedLanguage: '',
            clock: {
                date: '00-00-0000',
                time: '--:--',
            }
        }
    },
    mounted() {
        setInterval(() => {
            this.clock_ticker();
        }, 1000);
        document.addEventListener('click', this.handleOutsideClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
    },
    created() {
        this.logo = 'https://cdn.booqi.app/' + this.getOrganisation.appearance.images.logo;
        this.selectedLanguage = this.getLanguage || this.getLanguageCode(this.getPOS?.current_language) || '';
    },
    methods: {
        getNestedValue(source, path) {
            return path.split('.').reduce((value, key) => {
                if (value && typeof value === 'object' && key in value) {
                    return value[key];
                }

                return undefined;
            }, source);
        },
        getTranslation(paths, fallback) {
            const translations = this.getPOS?.translations;

            for (const path of paths) {
                if (translations && typeof translations[path] === 'string' && translations[path].trim()) {
                    return translations[path];
                }

                const value = this.getNestedValue(translations, path);

                if (typeof value === 'string' && value.trim()) {
                    return value;
                }
            }

            return fallback;
        },
        getLanguageCode(language) {
            const normalize = (value) => {
                if (typeof value !== 'string') {
                    return null;
                }

                const normalized = value.trim().toLowerCase();

                if (!normalized) {
                    return null;
                }

                if (normalized === 'ja-jp') {
                    return 'ja';
                }

                return normalized;
            };

            if (typeof language === 'string') {
                return normalize(language);
            }

            if (!language || typeof language !== 'object') {
                return null;
            }

            return [
                language.code,
                language.language,
                language.locale,
                language.key
            ]
                .map((value) => normalize(value))
                .find(Boolean) || null;
        },
        getLanguageLabel(language) {
            const code = this.getLanguageCode(language);

            if (!code) {
                return '';
            }

            const translatedLabel = this.getTranslation([
                `language_labels.${code}`,
                `language_picker.language_labels.${code}`,
                `languages.${code}`
            ], null);

            if (translatedLabel) {
                return translatedLabel;
            }

            if (language && typeof language === 'object') {
                return language.label || language.name || language.title || code.toUpperCase();
            }

            return code.toUpperCase();
        },
        getLanguageFlag(code) {
            const supportedFlags = ['de', 'en', 'es', 'fr', 'it', 'ja', 'nl'];

            const assetCode = code === 'ja' ? 'jp' : code;

            if (!supportedFlags.includes(code)) {
                return null;
            }

            return `img/languages/${assetCode}.svg`;
        },
        getLanguageShortLabel(code) {
            return (code || '?').slice(0, 2).toUpperCase();
        },
        reload() {
            const clickable = ['splash', 'catalog', 'booking'];

            if (clickable.includes(this.$route.name)) {
                window.location.replace('/');
            }
        },
        clock_ticker() {
            const now = new Date();
            this.clock.date = now.getDate() + '-' + (now.getMonth() + 1) + '-' + now.getFullYear();
            this.clock.time = now.getHours() + ':' + String(now.getMinutes()).padStart(2, '0');
        },
        toggleLanguagePicker() {
            this.isLanguagePickerOpen = !this.isLanguagePickerOpen;
        },
        closeLanguagePicker() {
            this.isLanguagePickerOpen = false;
        },
        selectLanguage(languageCode) {
            this.closeLanguagePicker();

            if (!languageCode || languageCode === this.getLanguage) {
                return;
            }

            this.selectedLanguage = languageCode;
            this.$emit('language-change', languageCode);
        },
        handleOutsideClick(event) {
            if (!this.isLanguagePickerOpen) {
                return;
            }

            if (!this.$el.contains(event.target)) {
                this.closeLanguagePicker();
            }
        }
    },
    computed: {
        ...mapGetters([
            'getOrganisation',
            'getPOS',
            'getLanguage'
        ]),
        supportedLanguages() {
            const supportedLanguages = Array.isArray(this.getPOS?.supported_languages)
                ? this.getPOS.supported_languages
                : [];

            return supportedLanguages
                .map((language) => {
                    const code = this.getLanguageCode(language);

                    if (!code) {
                        return null;
                    }

                    return {
                        code,
                        label: this.getLanguageLabel(language),
                        flag: this.getLanguageFlag(code),
                        shortLabel: this.getLanguageShortLabel(code)
                    };
                })
                .filter(Boolean);
        },
        currentLanguageOption() {
            return this.supportedLanguages.find((language) => language.code === this.selectedLanguage) || null;
        },
        showLanguagePicker() {
            return this.supportedLanguages.length > 1;
        },
        pickerLabel() {
            return this.getTranslation([
                'language_picker.label',
                'languagePicker.label',
                'picker.label'
            ], 'Language');
        },
        pickerPlaceholder() {
            return this.getTranslation([
                'language_picker.placeholder',
                'languagePicker.placeholder',
                'picker.placeholder'
            ], 'Select language');
        },
        pickerAccessibilityLabel() {
            return this.getTranslation([
                'language_picker.accessibility_label',
                'language_picker.aria_label',
                'languagePicker.accessibilityLabel',
                'languagePicker.ariaLabel',
                'picker.accessibility_label'
            ], this.pickerLabel);
        }
    },
    watch: {
        getLanguage(newLanguage) {
            this.selectedLanguage = newLanguage || '';
            this.closeLanguagePicker();
        }
    }
};
</script>
